export default function LafayetteHelpfulResourcesSection() {
  const photoLocations = [
    { name: "CVS Pharmacy — Johnston St", address: "4024 Johnston St, Lafayette, LA 70503", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Pinhook Rd", address: "3815 Pinhook Rd, Lafayette, LA 70503", hours: "Call for hours" },
    { name: "CVS Pharmacy — Ambassador Caffery", address: "3555 Ambassador Caffery Pkwy, Lafayette, LA 70503", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Kaliste Saloom", address: "110 Kaliste Saloom Rd, Lafayette, LA 70508", hours: "Call for hours" },
    { name: "The UPS Store — Acadiana", address: "3100 Johnston St, Lafayette, LA 70503", hours: "Call for hours" },
    { name: "Walgreens — Youngsville Hwy", address: "2205 Youngsville Hwy, Lafayette, LA 70508", hours: "Call for hours" },
  ];

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Lafayette Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Acadiana resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Lafayette</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Lafayette include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {photoLocations.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-list-3-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Obtaining Certified Documents in Louisiana</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Certified copies of vital records are required for passport applications — especially for first-time applicants, name changes, and minors. Here&apos;s where Louisiana residents can obtain them:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Louisiana Department of Health Vital Records Registry. Online, mail, or in-person options available for all Louisiana parishes.", url: "https://ldh.la.gov/page/651" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Marriage license records are maintained by the parish clerk of court where the license was issued. Contact your parish clerk for certified copies needed for passport name change applications.", url: "https://ldh.la.gov/page/vital-records" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Certified divorce records are available through the parish clerk of court where the divorce was granted. Required for name restoration passport corrections.", url: "https://ldh.la.gov/page/vital-records" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Certified death certificates through the LA Dept. of Health Vital Records Registry. Required for estate and legal matters.", url: "https://ldh.la.gov/page/652" },
                ].map((item, i) => (
                  <a key={i} href={item.url} target="_blank" rel="noopener noreferrer nofollow" className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 flex items-start gap-3 hover:border-[#F6C344]/50 transition-colors">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#1A365D] text-base`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-[#1A365D] text-xs leading-snug">{item.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mt-1">{item.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Order via VitalChek
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Lafayette Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { code: "LFT", name: "Lafayette Regional Airport", distance: "3 miles south of downtown", address: "200 Terminal Dr, Lafayette, LA 70508", note: "Primary airport. Nonstop connections to Dallas-Fort Worth, Atlanta, Charlotte, and Houston for international connections.", featured: true },
                  { code: "MSY", name: "Louis Armstrong New Orleans International", distance: "~130 miles east of Lafayette", address: "900 Airline Dr, Kenner, LA 70062", note: "Major international gateway with direct transatlantic routes to Europe. Many Acadiana residents drive to MSY for international flights.", featured: false },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Local</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs mb-1">{airport.name}</p>
                    <p className="text-[#F6C344] text-xs font-semibold mb-1">{airport.distance}</p>
                    <p className="text-gray-500 text-xs mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed">{airport.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing will not make it — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}